import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Ticket, Film, Tag, Camera, Building2, DollarSign } from 'lucide-react'
import BroadcastEntitlement from './BroadcastEntitlement'
import BroadcastRateCards from './BroadcastRateCards'
import BroadcastLibrary from './BroadcastLibrary'
import BroadcastReviewPanel from './BroadcastReview'

type TabId = 'entitlement' | 'broadcasts' | 'rates' | 'hints' | 'screenshots'

interface Tab {
  id: TabId
  name: string
  icon: React.ComponentType<{ className?: string }>
  /** Tabs land with the phase that builds them; shown disabled until then so the
   *  shape of the page is honest rather than appearing to lose features later. */
  availableFrom?: string
}

const TABS: Tab[] = [
  { id: 'broadcasts', name: 'Broadcasts', icon: Film },
  { id: 'entitlement', name: 'Entitlement', icon: Ticket },
  // Beside Entitlement on purpose: one is what the customer bought, the other
  // is what their exposure is worth. Both are commercial, neither is measured.
  { id: 'rates', name: 'Rate cards', icon: DollarSign },
  { id: 'hints', name: 'Brand hints', icon: Tag, availableFrom: 'analysis' },
  { id: 'screenshots', name: 'Screenshots', icon: Camera, availableFrom: 'capture' }
]

/**
 * Broadcast Admin — scoped to ONE customer, entered from the organization
 * drawer so there is never any doubt which customer is being administered.
 *
 * The customer id lives in the ROUTE rather than in selected-customer state:
 * a refresh keeps context, the link is shareable, and there is no hidden
 * global to fall out of sync.
 *
 * Platform-wide lookups (sports, leagues, teams, venues) are deliberately NOT
 * here — they live under /support/reference. A page headed with one customer's
 * name must not contain controls that change every customer.
 */
const BroadcastAdminPage: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const orgName = params.get('org') || undefined

  // Anything unrecognised falls back to the list rather than rendering an
  // empty frame - `?tab=review` links predate this page and are still in
  // people's history, and `review` is no longer a tab.
  const asked = params.get('tab')
  const initial: TabId = TABS.some((t) => t.id === asked) ? (asked as TabId) : 'broadcasts'
  const [activeTab, setActiveTab] = useState<TabId>(initial)

  /**
   * A broadcast in the URL means we are DRILLED IN to that one, and the page
   * becomes about it alone - no tab strip, no picker.
   *
   * Review used to be a tab, which made it a sibling of Broadcasts rather
   * than a child of a row in it: you could be on the Review tab with a
   * different broadcast selected than the one you clicked, and the only way
   * to reach a published one was a dropdown the list never pointed at. It is
   * a detail view, so it is addressed like one.
   */
  const drilledId = params.get('broadcast')

  const openReview = (id: string) => {
    const next = new URLSearchParams(params)
    next.set('broadcast', id)
    next.delete('tab')
    setParams(next)          // a real history entry, so Back leaves the detail
  }

  const exitReview = () => {
    const next = new URLSearchParams(params)
    next.delete('broadcast')
    next.set('tab', 'broadcasts')
    setParams(next, { replace: true })
    setActiveTab('broadcasts')
  }

  // no resolvable customer => back to the picker rather than defaulting to one
  useEffect(() => {
    if (!customerId) navigate('/support', { replace: true })
  }, [customerId, navigate])

  if (!customerId) return null

  const selectTab = (id: TabId) => {
    setActiveTab(id)
    const next = new URLSearchParams(params)
    next.set('tab', id)
    setParams(next, { replace: true })
  }

  if (drilledId) {
    return (
      <div className="min-h-screen bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* The customer line stays even here. Support act on behalf of
              customers all day and a page of brand decisions with no owner on
              it is how the wrong broadcast gets published. */}
          <div className="mb-5">
            <button
              onClick={exitReview}
              className="inline-flex items-center text-xs font-medium text-gray-500 hover:text-gray-800 mb-2 transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to Broadcasts
            </button>
            <p className="text-xs text-gray-500 flex items-center gap-1.5">
              <Building2 className="h-3 w-3" />
              {orgName && <span className="font-medium text-gray-700">{orgName}</span>}
              <span className="font-mono">{customerId}</span>
            </p>
          </div>

          {/* The panel owns the broadcast title, the run detail and the
              publish control - everything on this page is about this one. */}
          <BroadcastReviewPanel customerId={customerId} broadcastId={drilledId} />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* persistent customer context — support act on behalf of customers all
            day, so the one thing that must never be ambiguous is which one */}
        <div className="mb-5">
          <button
            onClick={() => navigate('/support')}
            className="inline-flex items-center text-xs font-medium text-gray-500 hover:text-gray-800 mb-3 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to Support
          </button>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <Film className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 truncate">
                Broadcasts{orgName ? ` — ${orgName}` : ''}
              </h1>
              <p className="text-xs text-gray-500 flex items-center gap-1.5">
                <Building2 className="h-3 w-3" />
                <span className="font-mono">{customerId}</span>
              </p>
            </div>
          </div>
        </div>

        <div className="flex space-x-1 bg-gradient-to-r from-gray-100 to-gray-200 p-1 rounded-lg shadow-inner mb-5 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon
            const disabled = Boolean(tab.availableFrom)
            return (
              <button
                key={tab.id}
                onClick={() => !disabled && selectTab(tab.id)}
                disabled={disabled}
                title={disabled ? `Arrives with ${tab.availableFrom}` : undefined}
                className={`flex items-center px-4 py-2 rounded-md text-xs font-semibold transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-white text-blue-600 shadow-md border border-white/50'
                    : disabled
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                <Icon className="h-3.5 w-3.5 mr-1.5" />
                {tab.name}
              </button>
            )
          })}
        </div>

        {activeTab === 'broadcasts' && (
          <BroadcastLibrary
            customerId={customerId}
            organizationName={orgName}
            // The id rides in the URL, so the broadcast a support user is
            // working on survives a refresh and can be handed to a colleague
            // as a link.
            onReview={(b) => openReview(b.id)}
          />
        )}

        {activeTab === 'rates' && <BroadcastRateCards customerId={customerId} />}

        {activeTab === 'entitlement' && (
          <BroadcastEntitlement customerId={customerId} organizationName={orgName} />
        )}
      </div>
    </div>
  )
}

export default BroadcastAdminPage
